/**
 * Dicionário do shell e do dashboard.
 *
 * O PRD fixa a interface em português do Brasil (seção 8); o inglês existe
 * para o atalho de idioma do header e cobre o shell, o dashboard e os
 * rótulos operacionais. O conteúdo das telas de processo segue em pt-BR.
 */

export const Locale = {
  PT_BR: 'pt-BR',
  EN: 'en',
} as const;
export type Locale = (typeof Locale)[keyof typeof Locale];

export const LOCALE_LABELS: Record<Locale, string> = {
  'pt-BR': 'Português (Brasil)',
  en: 'English',
};

const ptBR = {
  nav: {
    operation: 'Operação',
    execution: 'Execução',
    management: 'Gestão',
    registers: 'Cadastros',
    system: 'Sistema',
    dashboard: 'Dashboard',
    fleet: 'Painel da frota',
    events: 'Eventos',
    triage: 'Triagem',
    queue: 'Fila',
    field: 'Socorro',
    scheduling: 'Preventiva',
    operations: 'Plantão',
    workOrders: 'Ordens de serviço',
    stock: 'Estoque',
    metrics: 'Indicadores',
    km: 'Quilometragem',
    technicians: 'Técnicos',
    daily: 'Daily 15 min',
    dailyCheck: 'Checklist diário',
    partsLab: 'Qualidade de peças',
    suppliers: 'Fornecedores',
    audits: 'Auditorias',
    components: 'Vida de componentes',
    vehicles: 'Frota',
    catalog: 'Catálogo de falhas',
    users: 'Usuários',
    plans: 'Planos preventivos',
    materials: 'Materiais',
    designKit: 'Design kit',
    collapse: 'Recolher menu',
    expand: 'Expandir menu',
    openMenu: 'Abrir menu',
    closeMenu: 'Fechar menu',
  },
  header: {
    home: 'Início',
    language: 'Idioma',
    theme: 'Tema',
    themeLight: 'Claro',
    themeDark: 'Escuro',
    themeSystem: 'Sistema',
    switchToDark: 'Mudar para o tema escuro',
    switchToLight: 'Mudar para o tema claro',
    switchToEn: 'Switch to English',
    switchToPt: 'Mudar para português',
    contrast: 'Alto contraste',
    notifications: 'Notificações',
    noNotifications: 'Nenhuma notificação nova.',
    markRead: 'Marcar como lida',
    profile: 'Minha conta',
    signOut: 'Sair',
    signedAs: 'Conectado como',
    notificationsOff: 'Notificações desligadas nas preferências.',
    notificationsOffLink: 'Ligar',
    shortcuts: 'Personalizar atalhos',
    shortcutsHelp: 'Monte seus atalhos do cabeçalho escolhendo até {n} telas do menu lateral.',
    yourShortcuts: 'Seus atalhos',
    yourShortcutsHint: 'Arraste para definir a ordem no cabeçalho.',
    noShortcuts: 'Nenhum atalho escolhido ainda.',
    addShortcut: 'Adicionar atalho',
    addShortcutHint: 'Escolha acessos rápidos entre as telas disponíveis para o seu perfil.',
    shortcutsFull: 'Limite de {n} atalhos. Remova um para incluir outro.',
    shortcutsSaved: 'Atalhos salvos',
    add: 'Adicionar',
    remove: 'Remover',
    moveLeft: 'Mover para a esquerda',
    moveRight: 'Mover para a direita',
    back: 'Voltar',
    save: 'Salvar',
    simulator: 'Simulação',
    simulatorOn: 'Simulação ligada',
    simulatorOff: 'Simulação desligada',
    simulatorTitle: 'Simulador de demonstração',
    simulatorHelp: 'Ligar monta a garagem de demonstração (seis meses de histórico e a oficina em movimento) e, daí em diante, ela anda sozinha a cada 5 minutos pela própria API. Desligar apaga tudo que é demonstração e deixa só os dados reais.',
    simulatorBuilding: 'Montando simulação…',
    simulatorClearing: 'Limpando simulação…',
    simulatorPhaseHint: 'leva alguns minutos; a tela recarrega sozinha ao terminar',
    simulatorBuilt: 'Garagem de demonstração montada',
    simulatorCleared: 'Demonstração removida: só dados reais',
    simulatorPhaseFailed: 'A simulação não terminou',
    simulatorFailed: 'Simulação falhou',
    simulatorFailedHint: 'A última montagem falhou e foi desfeita; o banco voltou aos dados reais. Ligue de novo para tentar outra vez.',
    simulatorToastHint: 'A garagem está sendo preparada em segundo plano.',
    simulatorSwitchHint: 'vale para todos os usuários e sobrevive a reinícios',
    simulatorAdvance: 'Avançar a garagem agora',
    simulatorAdvancing: 'Avançando…',
    simulatorLastPulse: 'Último pulso',
    simulatorNoPulse: 'Nenhum pulso ainda.',
    simulatorNothing: 'Nada a fazer neste pulso: fora do horário de operação ou garagem em dia.',
    simulatorEnvHint: 'Estado atual vem do .env do servidor; o ajuste feito aqui prevalece.',
    simulatorToastOn: 'Simulador ligado',
    simulatorToastOff: 'Simulador desligado',
    simulatorToastPulse: 'Garagem avançada',
  },
  profile: {
    eyebrow: 'Minha conta',
    title: 'Configurações da conta',
    subtitle: 'Gerencie seu perfil, suas preferências e o histórico da conta.',
    dataTitle: 'Dados pessoais',
    name: 'Nome',
    phone: 'Telefone',
    email: 'E-mail',
    registration: 'Matrícula',
    adminOnly: 'E-mail, matrícula e perfil de acesso só o administrador altera (RF-36).',
    changePhoto: 'Trocar foto',
    removePhoto: 'Remover',
    photoHint: 'JPG, PNG ou WebP. A imagem é recortada em quadrado e reduzida a 256 px.',
    notImage: 'Escolha um arquivo de imagem.',
    save: 'Salvar alterações',
    saving: 'Salvando…',
    saved: 'Alterações salvas.',
    saveError: 'Não foi possível salvar.',
    passwordTitle: 'Senha',
    passwordHint: 'ao trocar, as outras sessões abertas são encerradas',
    currentPassword: 'Senha atual',
    newPassword: 'Nova senha',
    confirmPassword: 'Confirmar nova senha',
    passwordMismatch: 'A confirmação não confere com a nova senha.',
    passwordError: 'Não foi possível trocar a senha.',
    passwordChanged: 'Senha alterada.',
    changePassword: 'Trocar senha',
    infoTitle: 'Informações do perfil',
    infoHint: 'Seus dados da conta e foto de perfil',
    clickToChange: 'Clique para alterar',
    fullName: 'Nome completo',
    role: 'Função',
    status: 'Status da conta',
    active: 'Ativo',
    inactive: 'Inativo',
    memberSince: 'Membro desde',
    summaryTitle: 'Resumo da conta',
    summaryHint: 'Visão geral da atividade da sua conta',
    actionsMonth: 'Ações registradas',
    eventsMonth: 'Eventos abertos por você',
    thisMonth: 'este mês',
    lastLogin: 'Último login',
    sessions: 'Sessões ativas',
    never: 'Nunca',
    prefsTitle: 'Preferências',
    prefsHint: 'Personalize sua experiência no aplicativo',
    darkMode: 'Modo escuro',
    darkModeHint: 'Alternar entre temas claro e escuro',
    notifications: 'Notificações',
    notificationsHint: 'Receber notificações do sistema no cabeçalho',
    toastPosition: 'Posição dos avisos',
    toastPositionHint: 'Define onde os avisos rápidos aparecem na tela. Salvo automaticamente.',
    positions: { 'top-left': 'Superior esquerdo', 'top-right': 'Superior direito', 'bottom-left': 'Inferior esquerdo', 'bottom-right': 'Inferior direito' },
    prefsSaved: 'Preferências salvas',
    activityTitle: 'Atividades da conta',
    activityHint: 'Histórico de segurança e alterações de perfil.',
    activityAll: 'Tudo',
    activityEmpty: 'Nenhuma atividade registrada neste filtro.',
    categories: { security: 'Segurança', profile: 'Perfil', preferences: 'Preferências', system: 'Sistema' },
    today: 'Hoje',
    yesterday: 'Ontem',
    actions: {
      'auth.login': 'Login realizado',
      'auth.logout': 'Saída da conta',
      'user.password': 'Senha alterada',
      'user.first-access': 'Senha definida no primeiro acesso',
      'user.password-reset': 'Senha redefinida por link',
      'user.reset-link': 'Link de redefinição gerado',
      'user.profile': 'Perfil atualizado',
      'user.preferences': 'Preferências atualizadas',
      'user.create': 'Usuário criado',
      'user.update': 'Usuário alterado',
    },
  },
  dash: {
    eyebrow: 'Visão geral',
    title: 'Dashboard',
    subtitle: 'A garagem agora, e o histórico dos últimos {n} dias.',
    period: 'Período',
    periodDays: '{n} dias',
    breakdowns: 'quebras',
    updated: 'Atualizado',
    availability: 'Disponibilidade',
    availabilityHint: 'carros prontos ÷ frota',
    mkbf: 'MKBF',
    mkbfHint: 'km entre falhas',
    inQueue: 'Na fila',
    inQueueHint: 'aguardando valeta',
    openWo: 'OS abertas',
    openWoHint: 'com relógio correndo',
    overdue: 'estourada(s)',
    releasedIn: 'liberadas em {n} dias',
    vs7d: 'vs. 7 dias',
    noReturns: 'Sem retorno não programado no período — indicador em branco por honestidade.',
    mkbfTrend: 'MKBF por mês',
    mkbfTrendHint: 'km rodados ÷ retornos não programados',
    availByHour: 'Disponibilidade por faixa horária',
    availByHourHint: 'média dos últimos {n} dias · parado da quebra à liberação',
    fleetNow: 'A frota agora',
    fleetNowHint: 'cada carro no estado em que está · a chave vira o card e mostra a manutenção',
    fleetNowHintBig: 'a garagem carro a carro; quem está rodando, somado num cartão · a chave vira o card e mostra a manutenção',
    runningSummary: 'rodando agora',
    inLineShort: 'em linha',
    inYardShort: 'prontos no pátio',
    legendLabel: 'Legenda e filtro por família de estado',
    legendAll: 'Todos',
    legendHelp: {
      running: 'Em linha ou disponível no pátio',
      waiting: 'Aguardando triagem, socorro em campo ou na fila',
      working: 'Em manutenção, inspeção ou limpeza',
      stopped: 'Aguardando peça ou fora de operação',
    },
    legendEmpty: 'Nenhum carro nesta família agora.',
    flowTitle: 'Fluxo da garagem agora',
    flowHint: 'quantos carros em cada etapa — clique para abrir a etapa',
    flowLegend: { waiting: 'esperando', working: 'em serviço', stopped: 'parado por peça', empty: 'etapa vazia' },
    flowFamily: 'Família',
    stages: { triage: 'Triagem', field: 'Socorro', queue: 'Fila', shop: 'Oficina', part: 'Peça', release: 'Liberação' },
    stageHelp: {
      triage: 'Carro recolhido com falha apontada. O PCM decide se vai para a oficina, se a falha pode ser deferida ou se o carro volta à linha.',
      field: 'Carro parado na rua ou no ponto final. A equipe de socorro atende no local e decide se ele volta a rodar ou é recolhido.',
      queue: 'Carro já triado, com OS aberta, aguardando valeta e mecânico livres na oficina.',
      shop: 'Carro na valeta com serviço em andamento. O relógio da OS está correndo.',
      part: 'Serviço parado por falta de peça. O carro só volta quando o estoque atender a demanda.',
      release: 'Serviço concluído. O carro passa por inspeção final e limpeza antes de ser liberado para a operação.',
    },
    whatIs: 'O que é',
    stageStates: 'Estados',
    stageOpen: 'Clique no número para abrir',
    carNow: 'carro agora',
    carsNow: 'carros agora',
    of: 'de',
    readyBuses: 'carros prontos',
    target: 'meta',
    inService: 'na oficina (execução, peça e liberação)',
    oldest: 'mais antigo há',
    avg: 'média',
    perWo: 'por OS',
    overdueWo: 'OS com prazo estourado|OS com prazo estourado',
    noEvents: 'Nenhum evento registrado.',
    downtime: 'Tempo parado por causa',
    downtimeHint: 'horas das OS liberadas no período, por etapa que segurou o carro',
    eventsPerDay: 'Quebras por dia',
    eventsPerDayHint: 'retornos não programados · {n} dias · linha = média de 7 dias',
    eventsPerWeekHint: 'retornos não programados por semana · {n} dias · linha = média de 4 semanas',
    eventsPerWeek: 'Quebras por semana',
    recentEvents: 'Últimos eventos',
    attention: 'Precisa de atenção',
    attentionHint: 'clique para abrir a lista já filtrada',
    degradedKm: 'carro sem leitura de km há 2+ dias|carros sem leitura de km há 2+ dias',
    pendingTriage: 'evento aguardando triagem|eventos aguardando triagem',
    safetyOpen: 'falha de segurança em aberto|falhas de segurança em aberto',
    overdueParts: 'peça com prazo vencido|peças com prazo vencido',
    allClear: 'Nada pendente. A garagem está em dia.',
    seeAll: 'Ver todos',
    hours: 'h',
    days: 'd',
    total: 'Total',
    running: 'Rodando',
    waiting: 'Esperando',
    working: 'Em serviço',
    stopped: 'Parado',
    loading: 'Montando o dashboard…',
    error: 'Não foi possível carregar o dashboard.',
    staleError: 'A última atualização falhou; os números abaixo são da anterior.',
    errorRate: 'Muitas requisições em sequência; aguarde alguns segundos.',
    errorServer: 'O servidor não respondeu; tente de novo em instantes.',
    retry: 'Tentar de novo',
  },
};

export type Dictionary = typeof ptBR;

const en: Dictionary = {
  nav: {
    operation: 'Operations',
    execution: 'Execution',
    management: 'Management',
    registers: 'Registers',
    system: 'System',
    dashboard: 'Dashboard',
    fleet: 'Fleet panel',
    events: 'Events',
    triage: 'Triage',
    queue: 'Queue',
    field: 'Roadside',
    scheduling: 'Preventive',
    operations: 'Dispatch',
    workOrders: 'Work orders',
    stock: 'Stock',
    metrics: 'Metrics',
    km: 'Mileage',
    technicians: 'Technicians',
    daily: 'Daily 15 min',
    dailyCheck: 'Daily checklist',
    partsLab: 'Parts quality',
    suppliers: 'Suppliers',
    audits: 'Audits',
    components: 'Component life',
    vehicles: 'Fleet',
    catalog: 'Failure catalog',
    users: 'Users',
    plans: 'Maintenance plans',
    materials: 'Materials',
    designKit: 'Design kit',
    collapse: 'Collapse menu',
    expand: 'Expand menu',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
  },
  header: {
    home: 'Home',
    language: 'Language',
    theme: 'Theme',
    themeLight: 'Light',
    themeDark: 'Dark',
    themeSystem: 'System',
    switchToDark: 'Switch to dark theme',
    switchToLight: 'Switch to light theme',
    switchToEn: 'Switch to English',
    switchToPt: 'Mudar para português',
    contrast: 'High contrast',
    notifications: 'Notifications',
    noNotifications: 'No new notifications.',
    markRead: 'Mark as read',
    profile: 'My account',
    signOut: 'Sign out',
    signedAs: 'Signed in as',
    notificationsOff: 'Notifications are off in your preferences.',
    notificationsOffLink: 'Turn on',
    shortcuts: 'Customize shortcuts',
    shortcutsHelp: 'Build your header shortcuts by picking up to {n} screens from the side menu.',
    yourShortcuts: 'Your shortcuts',
    yourShortcutsHint: 'Drag to set the order in the header.',
    noShortcuts: 'No shortcut selected yet.',
    addShortcut: 'Add shortcut',
    addShortcutHint: 'Pick quick accesses among the screens available to your role.',
    shortcutsFull: 'Limit of {n} shortcuts. Remove one to add another.',
    shortcutsSaved: 'Shortcuts saved',
    add: 'Add',
    remove: 'Remove',
    moveLeft: 'Move left',
    moveRight: 'Move right',
    back: 'Back',
    save: 'Save',
    simulator: 'Simulation',
    simulatorOn: 'Simulation on',
    simulatorOff: 'Simulation off',
    simulatorTitle: 'Demo simulator',
    simulatorHelp: 'Turning it on builds the demo depot (six months of history and the workshop in motion) and from then on it runs by itself every 5 minutes through the API. Turning it off deletes everything that is demo and leaves only real data.',
    simulatorBuilding: 'Building simulation…',
    simulatorClearing: 'Clearing simulation…',
    simulatorPhaseHint: 'takes a few minutes; the screen reloads by itself when done',
    simulatorBuilt: 'Demo depot ready',
    simulatorCleared: 'Demo removed: real data only',
    simulatorPhaseFailed: 'The simulation did not finish',
    simulatorFailed: 'Simulation failed',
    simulatorFailedHint: 'The last build failed and was undone; the database is back to real data. Turn it on again to retry.',
    simulatorToastHint: 'The depot is being prepared in the background.',
    simulatorSwitchHint: 'applies to every user and survives restarts',
    simulatorAdvance: 'Advance the depot now',
    simulatorAdvancing: 'Advancing…',
    simulatorLastPulse: 'Last pulse',
    simulatorNoPulse: 'No pulse yet.',
    simulatorNothing: 'Nothing to do on this pulse: outside operating hours or depot up to date.',
    simulatorEnvHint: 'Current state comes from the server .env; a change made here takes precedence.',
    simulatorToastOn: 'Simulator on',
    simulatorToastOff: 'Simulator off',
    simulatorToastPulse: 'Depot advanced',
  },
  profile: {
    eyebrow: 'My account',
    title: 'Account settings',
    subtitle: 'Manage your profile, preferences and account history.',
    dataTitle: 'Personal data',
    name: 'Name',
    phone: 'Phone',
    email: 'E-mail',
    registration: 'Registration',
    adminOnly: 'E-mail, registration and role can only be changed by the administrator (RF-36).',
    changePhoto: 'Change photo',
    removePhoto: 'Remove',
    photoHint: 'JPG, PNG or WebP. The image is cropped square and resized to 256 px.',
    notImage: 'Pick an image file.',
    save: 'Save changes',
    saving: 'Saving…',
    saved: 'Changes saved.',
    saveError: 'Could not save.',
    passwordTitle: 'Password',
    passwordHint: 'changing it signs out your other sessions',
    currentPassword: 'Current password',
    newPassword: 'New password',
    confirmPassword: 'Confirm new password',
    passwordMismatch: 'Confirmation does not match the new password.',
    passwordError: 'Could not change the password.',
    passwordChanged: 'Password changed.',
    changePassword: 'Change password',
    infoTitle: 'Profile information',
    infoHint: 'Your account details and profile photo',
    clickToChange: 'Click to change',
    fullName: 'Full name',
    role: 'Role',
    status: 'Account status',
    active: 'Active',
    inactive: 'Inactive',
    memberSince: 'Member since',
    summaryTitle: 'Account summary',
    summaryHint: 'Overview of your account activity',
    actionsMonth: 'Actions recorded',
    eventsMonth: 'Events you opened',
    thisMonth: 'this month',
    lastLogin: 'Last login',
    sessions: 'Active sessions',
    never: 'Never',
    prefsTitle: 'Preferences',
    prefsHint: 'Customize your experience in the app',
    darkMode: 'Dark mode',
    darkModeHint: 'Switch between light and dark themes',
    notifications: 'Notifications',
    notificationsHint: 'Receive system notifications in the header',
    toastPosition: 'Toast position',
    toastPositionHint: 'Where quick notices appear on screen. Saved automatically.',
    positions: { 'top-left': 'Top left', 'top-right': 'Top right', 'bottom-left': 'Bottom left', 'bottom-right': 'Bottom right' },
    prefsSaved: 'Preferences saved',
    activityTitle: 'Account activity',
    activityHint: 'Security history and profile changes.',
    activityAll: 'All',
    activityEmpty: 'No activity recorded for this filter.',
    categories: { security: 'Security', profile: 'Profile', preferences: 'Preferences', system: 'System' },
    today: 'Today',
    yesterday: 'Yesterday',
    actions: {
      'auth.login': 'Signed in',
      'auth.logout': 'Signed out',
      'user.password': 'Password changed',
      'user.first-access': 'Password set on first access',
      'user.password-reset': 'Password reset by link',
      'user.reset-link': 'Reset link generated',
      'user.profile': 'Profile updated',
      'user.preferences': 'Preferences updated',
      'user.create': 'User created',
      'user.update': 'User changed',
    },
  },
  dash: {
    eyebrow: 'Overview',
    title: 'Dashboard',
    subtitle: 'The depot right now, and the history of the last {n} days.',
    period: 'Period',
    periodDays: '{n} days',
    breakdowns: 'breakdowns',
    updated: 'Updated',
    availability: 'Availability',
    availabilityHint: 'ready buses ÷ fleet',
    mkbf: 'MKBF',
    mkbfHint: 'km between failures',
    inQueue: 'In queue',
    inQueueHint: 'waiting for a bay',
    openWo: 'Open WOs',
    openWoHint: 'clock running',
    overdue: 'overdue',
    releasedIn: 'released in {n} days',
    vs7d: 'vs. 7 days',
    noReturns: 'No unscheduled return in the period — left blank on purpose.',
    mkbfTrend: 'MKBF by month',
    mkbfTrendHint: 'km travelled ÷ unscheduled returns',
    availByHour: 'Availability by hour',
    availByHourHint: '{n}-day average · down from failure to release',
    fleetNow: 'The fleet right now',
    fleetNowHint: 'every bus in its current state · the wrench flips the card to its maintenance',
    fleetNowHintBig: 'the garage bus by bus; buses on the road summed up in one card · the wrench flips the card to its maintenance',
    runningSummary: 'on the road now',
    inLineShort: 'in service',
    inYardShort: 'ready in the yard',
    legendLabel: 'Legend and filter by state family',
    legendAll: 'All',
    legendHelp: {
      running: 'In line or available in the yard',
      waiting: 'Awaiting triage, roadside service or in the queue',
      working: 'In maintenance, inspection or cleaning',
      stopped: 'Awaiting a part or out of service',
    },
    legendEmpty: 'No bus in this family right now.',
    flowTitle: 'Garage flow right now',
    flowHint: 'buses in each stage — click to open the stage',
    flowLegend: { waiting: 'waiting', working: 'in service', stopped: 'stopped for parts', empty: 'empty stage' },
    flowFamily: 'Family',
    stages: { triage: 'Triage', field: 'Roadside', queue: 'Queue', shop: 'Workshop', part: 'Parts', release: 'Release' },
    stageHelp: {
      triage: 'Bus pulled in with a reported failure. Planning decides whether it goes to the workshop, the failure is deferred, or the bus returns to service.',
      field: 'Bus stopped on the street or at the terminal. The roadside crew works on site and decides whether it keeps running or is pulled in.',
      queue: 'Bus already triaged, with an open work order, waiting for a free bay and mechanic.',
      shop: 'Bus in the bay with work in progress. The work-order clock is running.',
      part: 'Work halted for lack of a part. The bus only moves on when stock fills the demand.',
      release: 'Work done. The bus goes through final inspection and cleaning before being released to operations.',
    },
    whatIs: 'What is',
    stageStates: 'States',
    stageOpen: 'Click the number to open',
    carNow: 'bus right now',
    carsNow: 'buses right now',
    of: 'of',
    readyBuses: 'buses ready',
    target: 'target',
    inService: 'in the workshop (work, parts and release)',
    oldest: 'oldest for',
    avg: 'avg',
    perWo: 'per WO',
    overdueWo: 'work order past due|work orders past due',
    noEvents: 'No events recorded.',
    downtime: 'Downtime by cause',
    downtimeHint: 'hours from WOs released in the period, by the stage that held the bus',
    eventsPerDay: 'Breakdowns per day',
    eventsPerDayHint: 'unscheduled returns · {n} days · line = 7-day average',
    eventsPerWeekHint: 'unscheduled returns per week · {n} days · line = 4-week average',
    eventsPerWeek: 'Breakdowns per week',
    recentEvents: 'Latest events',
    attention: 'Needs attention',
    attentionHint: 'click to open the filtered list',
    degradedKm: 'bus with no km reading for 2+ days|buses with no km reading for 2+ days',
    pendingTriage: 'event awaiting triage|events awaiting triage',
    safetyOpen: 'open safety failure|open safety failures',
    overdueParts: 'part past due date|parts past due date',
    allClear: 'Nothing pending. The depot is on track.',
    seeAll: 'See all',
    hours: 'h',
    days: 'd',
    total: 'Total',
    running: 'Running',
    waiting: 'Waiting',
    working: 'In service',
    stopped: 'Stopped',
    loading: 'Building the dashboard…',
    error: 'Could not load the dashboard.',
    staleError: 'The last refresh failed; the numbers below are from the previous one.',
    errorRate: 'Too many requests in a row; wait a few seconds.',
    errorServer: 'The server did not answer; try again shortly.',
    retry: 'Try again',
  },
};

export const DICTIONARIES: Record<Locale, Dictionary> = { 'pt-BR': ptBR, en };
